import React from "react";
import { fmt, fmtPiles } from "@/lib/api";
import { Loader2 } from "lucide-react";

function Cell({ value, kind }) {
  if (!value) return <span className="text-slate-300 font-data">—</span>;
  const styles = {
    propre: "bg-green-50 text-green-800 border-green-200",
    sale: "bg-amber-50 text-amber-800 border-amber-200",
    rebut: "bg-slate-100 text-slate-600 border-slate-200",
    perte: "bg-red-50 text-red-700 border-red-200",
  }[kind];
  return (
    <span className={`inline-block rounded-md border px-2 py-0.5 font-data font-bold text-sm ${styles}`}>
      {value}
    </span>
  );
}

export default function MatrixStockTable({ matrix, title, testid, showSeuil = true, loading = false }) {
  const sites = [...new Set(matrix.map((m) => m.site))];
  const showPT = matrix.some((m) => m.perte_transport > 0);
  return (
    <div className="space-y-2">
      {title && (
        <h3 className="font-display font-extrabold text-base sm:text-lg text-slate-900 uppercase tracking-tight">{title}</h3>
      )}
      {loading ? (
        <div className="rounded-xl border-2 border-slate-200 bg-white p-6 flex items-center justify-center gap-2 text-slate-500 text-sm" data-testid={`${testid || "stock-matrix"}-loading`}>
          <Loader2 className="h-5 w-5 animate-spin text-blue-700" /> Chargement des stocks…
        </div>
      ) : matrix.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center text-slate-400 text-sm">
          Aucun site à afficher.
        </div>
      ) : (
      <div
        data-testid={testid || "stock-matrix-table"}
        className="overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-sm"
      >
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="bg-slate-900 text-white">
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide">Sites</th>
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide">Type</th>
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center">Propre <span className="text-[10px] normal-case text-slate-300">(piles)</span></th>
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center">Sale <span className="text-[10px] normal-case text-slate-300">(piles)</span></th>
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center">Rebut <span className="text-[10px] normal-case text-slate-300">(unités)</span></th>
            {showPT && (
              <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center" data-testid="matrix-col-perte-transport">Perte transp. <span className="text-[10px] normal-case text-slate-300">(unités)</span></th>
            )}
            <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center">Total <span className="text-[10px] normal-case text-slate-300">(piles)</span></th>
            {showSeuil && (
              <th className="px-3 py-3 font-display text-sm uppercase tracking-wide text-center">Seuil <span className="text-[10px] normal-case text-slate-300">(piles)</span></th>
            )}
          </tr>
        </thead>
        <tbody>
          {sites.map((site, siteIdx) => {
            const rows = matrix.filter((m) => m.site === site);
            return rows.map((r, idx) => (
              <tr
                key={r.site + r.packaging_type}
                className={`${idx === 0 && siteIdx > 0 ? "border-t-4 border-slate-800" : "border-t border-slate-200"} ${showSeuil && r.below_threshold ? "bg-red-50" : idx % 2 ? "bg-slate-50/60" : ""}`}
                data-testid={`matrix-row-${r.site}-${r.packaging_type}`}
              >
                {idx === 0 ? (
                  <td rowSpan={rows.length} className="px-3 py-2 align-top font-display font-extrabold text-slate-900 border-r border-slate-200">
                    {r.site_name}
                  </td>
                ) : null}
                <td className="px-3 py-2 font-semibold text-slate-700">{r.type_label}</td>
                <td className="px-3 py-2 text-center"><Cell value={r.propre ? fmtPiles(r.propre, r.pile_size) : 0} kind="propre" /></td>
                <td className="px-3 py-2 text-center"><Cell value={r.sale ? fmtPiles(r.sale, r.pile_size) : 0} kind="sale" /></td>
                <td className="px-3 py-2 text-center"><Cell value={r.rebut ? fmt(r.rebut) : 0} kind="rebut" /></td>
                {showPT && (
                  <td className="px-3 py-2 text-center" data-testid={`perte-transport-${r.site}-${r.packaging_type}`}><Cell value={r.perte_transport ? fmt(r.perte_transport) : 0} kind="perte" /></td>
                )}
                <td className="px-3 py-2 text-center font-data font-black text-slate-900">{fmtPiles(r.total, r.pile_size)}</td>
                {showSeuil && (
                  <td className="px-3 py-2 text-center">
                    <span className={`font-data text-sm font-bold ${r.below_threshold ? "text-red-600" : "text-slate-400"}`}>
                      {r.seuil_piles || 0}
                    </span>
                  </td>
                )}
              </tr>
            ));
          })}
        </tbody>
      </table>
      </div>
      )}
    </div>
  );
}
